/**
 * EdgenModalCreateAble.Root 在 RootLayoutClient 里经 lazyHydrate（idle + ~1500ms）延迟水合。
 * 首屏自动弹窗可能在 Root 挂载前 call，会抛 `No <Root> found`。
 *
 * 不再固定 sleep 1500ms（会拖慢用户点击后的正常弹窗）；
 * 改为立即 call，仅在 Root 未就绪时短间隔重试。
 */
export const EDGEN_MODAL_ROOT_DELAY_MS = 0;

const ROOT_READY_RETRY_MS = 250;
/** 覆盖 lazyHydrate 最坏 ~idle 3s + setTimeout 1.5s */
const ROOT_READY_MAX_RETRIES = 24;

export const isEdgenModalRootNotReadyError = (error: unknown): boolean =>
  error instanceof Error && error.message.includes("No <Root> found");

/** 可选预热 delay（默认 0）；Root 已挂载时不应阻塞弹窗 */
export const waitForEdgenModalRoot = (
  delayMs = EDGEN_MODAL_ROOT_DELAY_MS,
): Promise<void> =>
  delayMs > 0
    ? new Promise((resolve) => {
        window.setTimeout(resolve, delayMs);
      })
    : Promise.resolve();

/** 先尝试 call；Root 未就绪时短暂重试（react-call 需全局 Root） */
export const callEdgenModalWhenRootReadyAsync = async <T>(
  call: () => T | Promise<T>,
  delayMs = EDGEN_MODAL_ROOT_DELAY_MS,
): Promise<T> => {
  if (delayMs > 0) {
    await waitForEdgenModalRoot(delayMs);
  }

  let lastError: unknown;
  for (let attempt = 0; attempt <= ROOT_READY_MAX_RETRIES; attempt += 1) {
    try {
      return await call();
    } catch (error) {
      lastError = error;
      if (
        !isEdgenModalRootNotReadyError(error) ||
        attempt === ROOT_READY_MAX_RETRIES
      ) {
        throw error;
      }
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, ROOT_READY_RETRY_MS);
      });
    }
  }

  throw lastError;
};
